import { BookmarkIcon, CursorClickIcon } from '@heroicons/react/outline';
import {
  CogIcon,
  RefreshIcon,
  ShareIcon,
  VolumeOffIcon,
  VolumeUpIcon,
} from '@heroicons/react/solid';
import React, { useState } from 'react';
import { CompressArrowsIcon, ExpandArrowsIcon } from '../../app/icons/faIcons';
import { classNames } from '../../utils';
import * as styles from './buttons.module.css';

interface SpinButtonProps {
  onClick: () => void;
  disabled: boolean;
  pulseAnimation?: boolean;
  className?: string;
}

export const SpinButton: React.FC<SpinButtonProps> = ({
  onClick,
  disabled,
  pulseAnimation,
  className,
}) => {
  return (
    <div
      className={classNames('absolute', className)}
      style={{
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
      }}
    >
      <button
        aria-label="Spin the wheel"
        className={classNames(
          pulseAnimation ? styles.pulseAnimation : '',
          'bg-blue-600 border-8 border-white hover:bg-blue-700 text-white font-bold transition-[width_height] ease-in-out shadow-md disabled:shadow-sm p-4 rounded-full disabled:bg-green-300 disabled:cursor-not-allowed'
        )}
        style={{
          width: disabled ? '60px' : '120px',
          height: disabled ? '60px' : '120px',
        }}
        onClick={onClick}
        disabled={disabled}
      >
        {disabled ? (
          ''
        ) : (
          <>
            <CursorClickIcon className="h-11 w-11 mx-auto" />
            <span className="text-lg">Spin</span>
          </>
        )}
      </button>
    </div>
  );
};

interface VolumeButtonProps {
  volumeOn: boolean;
  onChange: (volumeOn: boolean) => void;
}

export const VolumeButton: React.FC<VolumeButtonProps> = ({
  volumeOn,
  onChange,
}) => {
  const [hover, setHover] = useState(false);
  const [clicked, setClicked] = useState(false);

  let visualStateVolumeOn = volumeOn;
  if (hover && !clicked) {
    visualStateVolumeOn = !volumeOn;
  }

  const handleClick = () => {
    onChange(!volumeOn);
    setClicked(true);
  };

  const handleMouseEnter = () => {
    setHover(true);
    setClicked(false);
  };

  const handleMouseLeave = () => {
    setHover(false);
  };

  return (
    <button
      aria-label={volumeOn ? 'Mute' : 'Unmute'}
      onClick={handleClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className={classNames(
        'w-12 h-12 flex items-center justify-center rounded-full border',
        'transition-colors duration-75 border-gray-200 shadow-md',
        visualStateVolumeOn ? 'bg-green-100' : 'bg-gray-200'
      )}
    >
      {visualStateVolumeOn ? (
        <VolumeUpIcon className="h-6 w-6 text-gray-700" />
      ) : (
        <VolumeOffIcon className="h-6 w-6 text-gray-700" />
      )}
    </button>
  );
};

interface FullscreenButtonProps {
  isFullscreen: boolean;
  onToggle: () => void;
}

export const FullscreenButton: React.FC<FullscreenButtonProps> = ({
  isFullscreen,
  onToggle,
}) => {
  return (
    <button
      aria-label={isFullscreen ? 'Exit full screen' : 'Full screen'}
      onClick={onToggle}
      className={classNames(
        'w-12 h-12 flex items-center justify-center rounded-full border',
        'transition-colors duration-75 border-gray-200 shadow-md',
        'bg-green-100 hover:bg-green-200',
        'group'
      )}
    >
      {isFullscreen ? (
        <CompressArrowsIcon
          size={1.5}
          className="text-gray-800 transition-transform group-hover:scale-90"
        />
      ) : (
        <ExpandArrowsIcon
          size={1.4}
          className="text-gray-800 transition-transform group-hover:scale-110"
        />
      )}
    </button>
  );
};

interface ShareButtonProps {
  onClick: () => void;
}

export const ShareButton: React.FC<ShareButtonProps> = ({ onClick }) => {
  return (
    <button
      aria-label="Share or embed"
      onClick={onClick}
      className={classNames(
        'w-12 h-12 flex items-center justify-center rounded-full border',
        'transition-colors duration-75 border-gray-200 shadow-md',
        'bg-green-100 hover:bg-green-200',
        'group'
      )}
    >
      <ShareIcon className="text-gray-800 w-6 h-6 transition-transform group-hover:scale-110" />
    </button>
  );
};

interface BookmarkButtonProps {
  onClick: () => void;
}

export const BookmarkButton: React.FC<BookmarkButtonProps> = ({ onClick }) => {
  return (
    <button
      aria-label="Instructions for saving (bookmarking) and loading a wheel"
      onClick={onClick}
      className={classNames(
        'w-12 h-12 flex items-center justify-center rounded-full border',
        'transition-colors duration-75 border-gray-200 shadow-md',
        'bg-green-100 hover:bg-green-200',
        'group'
      )}
    >
      <BookmarkIcon className="w-6 h-6 transition-transform group-hover:scale-110" />
    </button>
  );
};

interface SettingsButtonProps {
  onClick: () => void;
}

export const SettingsButton: React.FC<SettingsButtonProps> = ({ onClick }) => {
  return (
    <button
      aria-label="Settings"
      onClick={onClick}
      className={classNames(
        'w-12 h-12 flex items-center justify-center rounded-full border',
        'transition-colors duration-75 border-gray-200 shadow-md',
        'bg-green-100 hover:bg-green-200',
        'group'
      )}
    >
      <CogIcon className="text-gray-800 w-7 h-7 transition-transform group-hover:rotate-45 group-hover:text-black" />
    </button>
  );
};

interface ResetButtonProps {
  onClick: () => void;
}

export const ResetButton: React.FC<ResetButtonProps> = ({ onClick }) => {
  return (
    <button
      aria-label="Reset"
      onClick={onClick}
      className={classNames(
        'flex items-center justify-center px-4 py-2 rounded-full',
        'transition-colors duration-75 border border-gray-200 shadow-md',
        'bg-green-100 hover:bg-green-200',
        'group'
      )}
    >
      <span className="text-gray-800 text-base font-semibold">Reset</span>
      <RefreshIcon className="text-gray-800 w-6 h-6 ml-2 transition-transform group-hover:rotate-180" />
    </button>
  );
};
